import {
  InternalTransactions,
  LogLevel,
} from '@wix/panorama-common-shared/types';
import type {
  BaseParams,
  TransactionParams,
  InternalParams,
  ErrorUserData,
  TransactionUserData,
  EventPayload,
  TransactionEventPayload,
  ErrorEventPayload,
  LoggerParams,
  PhaseUserData,
  PhaseParams,
  PhaseEventPayload,
} from '@wix/panorama-common-shared/types';
import type { GlobalConfig } from '@wix/panorama-common/global-config';
import { extractWixHttpErrorDetails } from '../utils/error-utils';
import { sanitizePayload } from './payload-sanitizer';

const getSessionParams = (
  globalConfig: GlobalConfig,
): Pick<InternalParams, 'sessionId' | 'sessionTime'> => {
  const sessionId = globalConfig.getSessionId();
  const sessionTime = Math.round(globalConfig.getSessionTime());

  return { sessionId, sessionTime };
};

export const buildTransactionPayload = (
  globalConfig: GlobalConfig,
  baseParams: BaseParams,
  transactionParams: TransactionParams,
  data?: TransactionUserData,
): TransactionEventPayload => {
  const { transactionName, transactionAction } = transactionParams;

  const { sessionId, sessionTime } = getSessionParams(globalConfig);
  const logLevel = LogLevel.Info;
  const message = `Panorama ${transactionName} ${transactionAction}`;
  const requestId = '';

  return sanitizePayload({
    ...baseParams,
    ...transactionParams,
    sessionId,
    sessionTime,
    logLevel,
    requestId,
    message,
    data,
  });
};

export const buildPhasePayload = (
  globalConfig: GlobalConfig,
  baseParams: BaseParams,
  phaseParams: PhaseParams,
  data?: PhaseUserData,
): PhaseEventPayload => {
  const transactionName = InternalTransactions.ComponentPhase;
  const { phaseName, transactionAction } = phaseParams;

  const { sessionId, sessionTime } = getSessionParams(globalConfig);
  const logLevel = LogLevel.Info;
  const message = `Panorama ${phaseName} phase ${transactionAction}`;
  const requestId = '';

  return sanitizePayload({
    ...baseParams,
    ...phaseParams,
    transactionName,
    sessionId,
    sessionTime,
    logLevel,
    requestId,
    message,
    data,
  });
};

export const buildErrorPayload = (
  globalConfig: GlobalConfig,
  baseParams: BaseParams,
  error: Error,
  data?: ErrorUserData,
): ErrorEventPayload => {
  const { sessionId, sessionTime } = getSessionParams(globalConfig);
  const { constructor, message, stack = '' } = error;
  const logLevel = LogLevel.Error;
  const { requestId } = extractWixHttpErrorDetails(error);

  return sanitizePayload({
    ...baseParams,
    sessionId,
    sessionTime,
    logLevel,
    requestId,
    data,
    errorName: data?.errorName ?? constructor.name,
    errorStack: stack,
    message,
  });
};

export const buildLoggerPayload = (
  globalConfig: GlobalConfig,
  baseParams: BaseParams,
  loggerParams: LoggerParams,
): EventPayload => {
  const { sessionId, sessionTime } = getSessionParams(globalConfig);
  const requestId = '';

  return sanitizePayload({
    ...baseParams,
    ...loggerParams,
    sessionId,
    sessionTime,
    requestId,
  });
};
